import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList
} from '@angular/cdk/drag-drop';
import { LowerCasePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  QueryList,
  ViewChild,
  ViewChildren,
  input,
  output,
  signal
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { startWith } from 'rxjs';

import { ICONS } from '@core/services/icons/icons.service';

import { ColorOfIndex } from '@shared/pipes/color.pipe';

import {
  List,
  ListItem
} from '@features/components/visualization/list/models/list-visualization';

import { VisuItemLabel } from '../../common/models/label.model';

interface DragAndDropEvent {
  sourceElemValue: string;
  sourcePrimAggPos: string;
  targetElemValue: string;
  targetPrimAggPos: string;
}

@Component({
  selector: 'app-list',
  imports: [
    LowerCasePipe,
    CdkDragPreview,
    CdkDragPlaceholder,
    CdkDropList,
    CdkDrag,
    TranslatePipe,
    ColorOfIndex
  ],
  templateUrl: './list.component.html',
  styleUrl: './list.component.css'
})
export class ListComponent implements AfterViewInit {
  list = input.required<List>();
  enableButtonLinkToMap = input<boolean>(false);
  enableEdit = input<boolean>(false);
  enableExport = input<boolean>(false);
  enableMarkTaskAsDone = input<boolean>(false);
  fullScreen = input<boolean>(false);
  optionsMenu = input<boolean>(false);

  hide = output();
  exportOutput = output<{ primaryAggregatorId: number }>();
  dragAndDrop = output<DragAndDropEvent>();
  taskDone = output<{ appElementId: number; taskDone: boolean }>();

  @ViewChild('listElement', { static: false }) protected listElement!: ElementRef;
  @ViewChildren('listItemElement', { read: ElementRef })
  protected listItemElements: QueryList<ElementRef> | undefined;

  @HostBinding('style.width')
  protected width = this.fullScreen() ? '100%' : '24rem';
  @HostBinding('style.min-width') protected minWidth = this.fullScreen() ? '100%' : '16rem';
  @HostBinding('style.max-width') protected maxWidth = this.fullScreen() ? '100%' : '24rem';

  private listScrollTop = 0;
  private showDetailsState = signal<{ [appElementId: number]: boolean }>({});

  constructor() {}

  /**
   * Handle the drag and drop event for the list.
   *
   * @param {CdkDragDrop<{ aggragtorId: number; elemsIds: number[] }>} $event - The drag and
   *   drop event.
   */
  drop($event: CdkDragDrop<{ aggregatorId: string; elemsIds: number[] }>) {
    const sourcePrimAggPos = $event.previousContainer.data.aggregatorId;
    const sourceElemValue = String(
      $event.previousContainer.data.elemsIds[$event.previousIndex]
    );
    const targetPrimAggPos = $event.container.data.aggregatorId;
    const targetElemValue = String($event.container.data.elemsIds[$event.currentIndex]);

    this.dragAndDrop.emit({
      sourcePrimAggPos,
      sourceElemValue,
      targetElemValue,
      targetPrimAggPos
    });
  }

  /** Emit an event to indicate that the list should be hidden. */
  emitHide() {
    this.hide.emit();
  }

  /**
   * Emit an event to indicate that the list should be exported.
   *
   * @param {number} primaryAggregatorId - The ID of the primary aggregator to export.
   */
  exportPrimaryAggregator(primaryAggregatorId: number) {
    this.exportOutput.emit({ primaryAggregatorId });
  }

  /**
   * Filter the labels of a list item to only include those with level 0.
   *
   * @param {VisuItemLabel[]} labels - The labels to filter.
   * @returns {VisuItemLabel[]} - An array of labels with level 0.
   */
  filterLevel0Labels(labels: VisuItemLabel[]): VisuItemLabel[] {
    return labels.filter((label) => label.level === 0);
  }

  /**
   * Filter the labels of a list item to exclude those with level 0.
   *
   * @param {VisuItemLabel[]} labels - The labels to filter.
   * @returns {VisuItemLabel[]} - An array of labels with level greater than 0.
   */
  filterOtherLabels(labels: VisuItemLabel[]): VisuItemLabel[] {
    return labels.filter((label) => label.level !== 0);
  }

  /**
   * Check if a list item has any labels with level greater than 0.
   *
   * @param {VisuItemLabel[]} labels - The labels of the list item to check.
   * @returns {boolean} - Returns true if there are labels with level greater than 0,
   *   otherwise false.
   */
  hasDetails(labels: VisuItemLabel[]): boolean {
    return labels.some((label) => label.level !== 0);
  }

  ngAfterViewInit() {
    if (!this.listItemElements) {
      return;
    }

    this.listItemElements.changes.pipe(startWith([undefined])).subscribe(() => {
      this.listElement.nativeElement.scrollTop = this.listScrollTop;
    });
  }

  /**
   * Handle the scroll event of the list element to save the scroll position.
   *
   * @param {Event} $event
   */
  onScrollList($event: Event) {
    const listElement = $event.currentTarget;
    if (listElement instanceof HTMLElement) {
      this.listScrollTop = listElement.scrollTop;
    }
  }

  /**
   * Redirect to an external map provider with the coordinates of the list item.
   *
   * @param {ListItem} item - The list item containing the coordinates.
   */
  redirectToExternalMapProvider(item: ListItem) {
    const lat = item.coordinates?.lat;
    const long = item.coordinates?.lng;
    window.open('https://www.google.com/maps/place/' + lat + ',' + long);
  }

  toggleShowDetails(item: ListItem) {
    const currentState = this.showDetailsState();
    const currentValue = currentState[item.appElementId] || false;
    this.showDetailsState.update((state) => ({
      ...state,
      [item.appElementId]: !currentValue
    }));
  }

  isShowingDetails(item: ListItem): boolean {
    return this.showDetailsState()[item.appElementId] || false;
  }

  /**
   * Toggle the task done state of a list item.
   *
   * @param {ListItem} item - The list item to toggle.
   */
  toggleTaskDone(item: ListItem) {
    const taskDone = !item.taskDone;
    this.taskDone.emit({ appElementId: item.appElementId, taskDone });
  }

  protected getMarkerTaskClasses(item: ListItem): string {
    const baseClasses = 'px-3 py-1 rounded-md hover:bg-neutral-300 cursor-pointer';
    return item.taskDone
      ? `${baseClasses} bg-green-600 text-white`
      : `${baseClasses} text-black`;
  }

  protected readonly ICONS = ICONS;

  protected trackListItem(index: number, item: ListItem): string {
    return `${item.name}_${item.appElementId}_${index}`;
  }
}
